import { Component, OnInit, OnDestroy, HostBinding, HostListener, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, interval, combineLatest } from 'rxjs';
import { takeUntil, switchMap, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AccessLevels, Permissions } from '@bciti/types';
import { IPermissionScope } from 'src/app/auth/auth.model';
import { userScopesSelector } from 'src/app/user/store/user.selectors';
import { subSelector } from 'src/app/auth/store/auth.selectors';
import { State } from 'src/app/store';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';
import { Request } from '../../models/request.model';

@Component({
    selector: 'app-request-detail-page',
    templateUrl: './request-detail-page.component.html',
    styleUrls: ['./request-detail-page.component.scss'],
})
export class RequestDetailPageComponent implements OnInit, OnDestroy {
    @HostBinding('class.closing') closing = false;

    private destroy$ = new Subject<void>();
    private pollingSubscription: Subscription | null = null;
    private readonly POLLING_INTERVAL_MS = 15000;

    request: Request;
    loading = true;
    pollingMessages = false;
    canEdit = false;
    private hasChanges = false;

    private userScopes: IPermissionScope[] = [];
    private userId: string;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private requestsService: RequestsV2ImplService,
        private store: Store<State>,
        private elementRef: ElementRef,
    ) {}

    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(event: MouseEvent): void {
        if (this.closing || this.loading) return;
        if (this.elementRef.nativeElement.contains(event.target as Node)) return;
        if ((event.target as HTMLElement).closest('.cdk-overlay-container')) return;
        this.goBack();
    }

    ngOnInit(): void {
        combineLatest([this.store.select(userScopesSelector), this.store.select(subSelector)])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([scopes, userId]) => {
                this.userScopes = scopes || [];
                this.userId = userId;
                this.updateCanEdit();
            });

        this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            if (params['id']) {
                this.closing = false;
                this.stopPolling();
                this.loadRequest(params['id']);
            }
        });
    }

    loadRequest(id: string): void {
        this.loading = true;
        this.requestsService
            .getRequestDetails(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data: Request) => {
                    this.request = data;
                    this.updateCanEdit();
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    refreshRequest(id: string): void {
        this.requestsService
            .getRequestDetails(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data: Request) => {
                    this.request = data;
                    this.updateCanEdit();
                },
            });
    }

    handleRequestChanged(updatedRequest: Request): void {
        this.hasChanges = true;
        this.request = updatedRequest;
        this.refreshRequest(updatedRequest._id);
    }

    handleRefreshNeeded(): void {
        if (this.request?._id) {
            this.refreshRequest(this.request._id);
        }
    }

    onMessagesTabActivated(): void {
        this.startPolling();
    }

    onInfosTabActivated(): void {
        this.stopPolling();
        this.markMessagesAsRead();
    }

    private markMessagesAsRead(): void {
        if (!this.request?._id || !this.request.adminUnreadMessageCount) return;

        // Not piped through takeUntil(this.destroy$): this is called from ngOnDestroy itself,
        // and unsubscribing there would abort the underlying HTTP request before it completes.
        this.requestsService.markMessagesAsRead(this.request._id).subscribe({
            next: () => {
                this.request = { ...this.request, adminUnreadMessageCount: 0 };
            },
            error: () => {},
        });
    }

    private startPolling(): void {
        if (this.pollingSubscription || !this.request?._id) return;

        this.pollingSubscription = interval(this.POLLING_INTERVAL_MS)
            .pipe(
                filter(() => true),
                switchMap(() => {
                    this.pollingMessages = true;
                    return this.requestsService.getRequestDetails(this.request._id);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (data: Request) => {
                    this.request = {
                        ...this.request,
                        messages: data.messages,
                        adminUnreadMessageCount: data.adminUnreadMessageCount,
                    };
                    this.pollingMessages = false;
                },
                error: () => {
                    this.pollingMessages = false;
                },
            });
    }

    private stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
            this.pollingMessages = false;
        }
    }

    private updateCanEdit(): void {
        if (!this.request) {
            this.canEdit = false;
            return;
        }
        if (this.request.user?._id === this.userId) {
            this.canEdit = true;
            return;
        }
        if (!this.request.department) {
            this.canEdit = false;
            return;
        }
        this.canEdit = this.userScopes.some(
            (scope) =>
                scope.permission === Permissions.REQUEST &&
                scope.resource === 'department' &&
                (scope.resourceId === '*' || scope.resourceId === this.request.department._id) &&
                scope.accessLevel === AccessLevels.WRITE,
        );
    }

    handleNavigateToRequest(linkedRequest: Request): void {
        this.closing = true;
        setTimeout(() => {
            this.router.navigate(['/requests-v2/manage/details', linkedRequest._id], {
                queryParams: { from: this.request._id },
            });
        }, 240);
    }

    goBack(): void {
        this.closing = true;
        setTimeout(() => {
            const from = this.route.snapshot.queryParams['from'];
            if (from) {
                this.router.navigate(['/requests-v2/manage/details', from]);
            } else {
                this.router.navigate(['/requests-v2/manage'], {
                    state: { requestModified: this.hasChanges },
                });
            }
        }, 240);
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.markMessagesAsRead();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
